"use client";

/**
 * ImageCarousel
 * Interactive image carousel for the public view with navigation, auto-play,
 * and a zoom modal with pan/drag support.
 * Used by CarouselRenderer and VenueRenderer in public view.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus, X, ZoomIn } from "lucide-react";
import dynamic from "next/dynamic";
import { useMobile } from "@/hooks/use-mobile";
import type { ComponentImage } from "../../_types";
import { CAROUSEL_IMAGE_RENDER_WIDTH, LANDING_IMAGE_RENDER_QUALITY } from "../../_logic/constants";
import { transformStorageImageUrl } from "../../_logic/image-cdn";
import { isSafeUrl } from "../../_logic/utils";

// Lazy-load YARL + its CSS only when a mobile visitor opens the lightbox.
// Desktop bundles never include either.
const ImageCarouselMobileLightbox = dynamic(() => import("./ImageCarouselMobileLightbox"), { ssr: false });

/** Auto-play interval in milliseconds */
const AUTO_PLAY_INTERVAL = 5000;
/** Fallback aspect ratio (16:9) used while the first image is loading */
const FALLBACK_ASPECT_RATIO = 16 / 9;
/** Zoom level constraints */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
/** Horizontal touch distance (px) required to register as a swipe */
const SWIPE_THRESHOLD = 50;

interface ImageCarouselProps {
  /** Array of images to display */
  images: ComponentImage[];
  /** Optional CSS class for the outer container */
  className?: string;
}

/** Interactive image carousel with navigation, auto-play, and zoom modal */
export function ImageCarousel({ images, className }: ImageCarouselProps) {
  // States
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(FALLBACK_ASPECT_RATIO);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const wasSwipeRef = useRef(false);
  const isMobile = useMobile();

  // Derived values
  const safeImages = useMemo(() => images.filter((img) => isSafeUrl(img.url)), [images]);
  const isModalOpen = modalIndex !== null;
  const isLightboxOpen = lightboxIndex !== null;
  // Right-sized WebP for the in-page slides (full-res originals can be 10+ MB each).
  const slideUrls = useMemo(
    () =>
      safeImages.map((img) =>
        transformStorageImageUrl(img.url, {
          width: CAROUSEL_IMAGE_RENDER_WIDTH,
          quality: LANDING_IMAGE_RENDER_QUALITY,
        })
      ),
    [safeImages]
  );
  // Reuse the already-downloaded slide images so the zoom/lightbox opens instantly with no new fetch.
  const lightboxSlides = useMemo(
    () => safeImages.map((img, i) => ({ src: slideUrls[i], alt: img.name || undefined })),
    [safeImages, slideUrls]
  );

  // Handlers — main carousel navigation
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? safeImages.length - 1 : prev - 1));
  }, [safeImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === safeImages.length - 1 ? 0 : prev + 1));
  }, [safeImages.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrev();
      else if (e.key === "ArrowRight") goToNext();
    },
    [goToPrev, goToNext]
  );

  // Handlers — touch swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    wasSwipeRef.current = false;
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = null;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartXRef.current === null || touchEndXRef.current === null) {
      touchStartXRef.current = null;
      touchEndXRef.current = null;
      return;
    }
    const distance = touchStartXRef.current - touchEndXRef.current;
    if (Math.abs(distance) > SWIPE_THRESHOLD) {
      wasSwipeRef.current = true;
      if (distance > 0) goToNext();
      else goToPrev();
    }
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  }, [goToNext, goToPrev]);

  // Handlers — modal
  const openModal = useCallback((index: number) => {
    setModalIndex(index);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const closeModal = useCallback(() => {
    setModalIndex(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const modalPrev = useCallback(() => {
    setModalIndex((prev) => (prev === null ? null : prev === 0 ? safeImages.length - 1 : prev - 1));
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [safeImages.length]);

  const modalNext = useCallback(() => {
    setModalIndex((prev) => (prev === null ? null : prev === safeImages.length - 1 ? 0 : prev + 1));
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [safeImages.length]);

  // Handlers — zoom
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM)), []);

  // Handlers — pan/drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
      }
    },
    [zoom, pan]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || zoom <= 1) return;
      const newX = e.clientX - dragStartRef.current.x;
      const newY = e.clientY - dragStartRef.current.y;
      const container = e.currentTarget as HTMLElement;
      const maxX = (container.offsetWidth * (zoom - 1)) / 2;
      const maxY = (container.offsetHeight * (zoom - 1)) / 2;
      setPan({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY)),
      });
    },
    [isDragging, zoom]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  // Effects — detect aspect ratio from first image
  useEffect(() => {
    if (safeImages.length === 0) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    // Probe the transformed slide (same URL the first slide loads) so we reuse its cached
    // download instead of fetching the full-resolution original just to read its dimensions.
    img.src = slideUrls[0];
    // Key on the primitive URL, not the array identity, so a fresh `images` array reference from
    // the parent doesn't re-fire the probe when the first slide hasn't actually changed.
  }, [slideUrls[0]]);

  // Effects — auto-play (pauses on hover and when any viewer is open)
  useEffect(() => {
    if (safeImages.length <= 1 || isHovered || isModalOpen || isLightboxOpen) return;
    const timer = setInterval(goToNext, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [safeImages.length, isHovered, isModalOpen, isLightboxOpen, goToNext]);

  // Effects — escape key closes modal
  useEffect(() => {
    if (!isModalOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
      else if (e.key === "ArrowLeft") modalPrev();
      else if (e.key === "ArrowRight") modalNext();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isModalOpen, closeModal, modalPrev, modalNext]);

  // Render — empty state
  if (safeImages.length === 0) return null;

  return (
    <>
      {/* Main Carousel */}
      <div
        className={`group/carousel relative touch-pan-y ${className ?? ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        tabIndex={0}
        role="region"
        aria-label="Image carousel"
      >
        {/* Image Container — fixed 4:3 on narrow viewports (avoids overly short images),
            natural aspect ratio at @md+ via a CSS variable */}
        <div
          className="relative aspect-4/3 overflow-hidden rounded-2xl bg-gray-100 md:aspect-(--carousel-aspect)"
          style={{ "--carousel-aspect": aspectRatio.toString() } as React.CSSProperties}
        >
          {safeImages.map((image, index) => (
            <button
              key={image.id}
              type="button"
              onClick={() => {
                if (wasSwipeRef.current) {
                  wasSwipeRef.current = false;
                  return;
                }
                if (isMobile) setLightboxIndex(index);
                else openModal(index);
              }}
              className={`absolute inset-0 h-full w-full touch-manipulation transition-opacity duration-500 ${
                index === currentIndex ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
              aria-label={`View ${image.name || `slide ${index + 1}`}`}
            >
              <img
                src={slideUrls[index]}
                alt={image.name || `Slide ${index + 1}`}
                className="h-full w-full object-cover"
                loading={index === 0 ? undefined : "lazy"}
                decoding="async"
                draggable={false}
              />
            </button>
          ))}

          {/* Zoom hint — appears in corner on hover */}
          <div
            className="pointer-events-none absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white opacity-0 backdrop-blur-sm transition-opacity group-hover/carousel:opacity-100"
            aria-hidden="true"
          >
            <ZoomIn className="h-3 w-3" />
            <span>Zoom</span>
          </div>

          {/* Inline dot indicators — Airbnb-style, inset at bottom */}
          {safeImages.length > 1 && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1.5 backdrop-blur-sm">
                {safeImages.map((image, index) => (
                  <span
                    key={image.id}
                    className={`block h-1.5 rounded-full bg-white transition-all ${
                      index === currentIndex ? "w-4 opacity-100" : "w-1.5 opacity-60"
                    }`}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Arrows — hover-revealed, soft outlined white circles */}
        {safeImages.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-3 top-1/2 z-10 hidden h-14 w-14 touch-manipulation -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 md:flex md:h-9 md:w-9 md:opacity-0 md:group-hover/carousel:opacity-100"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-6 w-6 md:h-4 md:w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 top-1/2 z-10 hidden h-14 w-14 touch-manipulation -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 md:flex md:h-9 md:w-9 md:opacity-0 md:group-hover/carousel:opacity-100"
              aria-label="Next slide"
            >
              <ChevronRight className="h-6 w-6 md:h-4 md:w-4" />
            </button>
          </>
        )}
      </div>

      {/* Zoom Modal */}
      {isModalOpen && modalIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-label="Image zoom view">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 touch-manipulation bg-black/90"
            onClick={closeModal}
            aria-label="Close zoom view"
            tabIndex={-1}
          />

          {/* Close Button */}
          <button
            type="button"
            onClick={closeModal}
            className="absolute right-4 top-4 z-50 touch-manipulation rounded-lg p-2 text-white transition-colors hover:text-gray-300"
            aria-label="Close"
          >
            <X className="h-7 w-7" />
          </button>

          {/* Zoom Controls */}
          <div
            className="absolute left-4 top-4 z-50 flex items-stretch overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
            role="group"
            aria-label="Zoom controls"
          >
            <ZoomButton onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">
              <Minus className="h-3.5 w-3.5" />
            </ZoomButton>
            <div className="w-px bg-gray-200" />
            <div className="flex h-8 min-w-12 items-center justify-center px-2 text-tag-sm font-medium tabular-nums text-gray-700">
              {Math.round(zoom * 100)}%
            </div>
            <div className="w-px bg-gray-200" />
            <ZoomButton onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
              <Plus className="h-3.5 w-3.5" />
            </ZoomButton>
            <span className="sr-only" aria-live="polite">
              Zoom {Math.round(zoom * 100)} percent
            </span>
          </div>

          {/* Modal Navigation */}
          {safeImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  modalPrev();
                }}
                className="absolute left-4 top-1/2 z-50 flex h-11 w-11 touch-manipulation -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  modalNext();
                }}
                className="absolute right-4 top-1/2 z-50 flex h-11 w-11 touch-manipulation -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {/* Zoomable Image */}
          <div
            className="flex max-h-full max-w-full items-center justify-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
          >
            <img
              src={slideUrls[modalIndex]}
              alt={safeImages[modalIndex].name || `Image ${modalIndex + 1}`}
              className="max-w-none select-none transition-transform duration-200"
              draggable={false}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: "center center",
              }}
            />
          </div>

          {/* Image Counter */}
          {safeImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-black/50 px-4 py-2 font-medium text-white">
              {modalIndex + 1} / {safeImages.length}
            </div>
          )}
        </div>
      )}

      {/* Mobile lightbox — touch-native swipe + pinch-zoom. Mounted only on mobile so
          desktop never downloads YARL. */}
      {isMobile && (
        <ImageCarouselMobileLightbox
          open={isLightboxOpen}
          close={() => setLightboxIndex(null)}
          index={lightboxIndex ?? 0}
          slides={lightboxSlides}
        />
      )}
    </>
  );
}

interface ZoomButtonProps {
  onClick: () => void;
  disabled: boolean;
  "aria-label": string;
  children: React.ReactNode;
}

function ZoomButton({ onClick, disabled, "aria-label": ariaLabel, children }: ZoomButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex h-8 w-8 touch-manipulation items-center justify-center text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
